import {EditorContent, EditorContext, useEditor} from "@tiptap/react"
import content from "./testcontent.json"
import {MenuBar} from "./MenuBar/MenuBar"
/* TipTap Extensions */
import {StarterKit} from "@tiptap/starter-kit" // starter kit is a bundle of frequently used extensions, like Text, Bold, Lists, etc. For a full list: https://tiptap.dev/docs/editor/extensions/functionality/starterkit#included-extensions
import {Collaboration} from "@tiptap/extension-collaboration"
import {Image} from "@tiptap/extension-image"
import {ImageResize} from "tiptap-extension-resize-image"
import {FileHandler} from "@tiptap/extension-file-handler"
import {Highlight} from "@tiptap/extension-highlight"
import {TextStyleKit} from "@tiptap/extension-text-style"
import axios from "axios"
import {useAuth0} from "@auth0/auth0-react"
import {useMemo, useState} from "react"
import {Alert, Button} from "@mui/material"


export function Editor({provider, doc, isSynching, connectionError, lastUpdated}) {
	const {getAccessTokenSilently} = useAuth0()
	const [canEdit, setCanEdit] = useState(true)
	const [documentDeleted, setDocumentDeleted] = useState(false)

	const uploadFile = async (fileList) => {
		const token = await getAccessTokenSilently()

		const formData = new FormData()
		formData.append("editorfile", fileList[0])
		formData.append("docname", provider.configuration.name)

		try {
			const res= await axios.post("/api/rte/file", formData, {
				headers: {
					"Authorization": "Bearer " + token,
					"Content-Type": "multipart/form-data"
				}
			})
			return res.data
		} catch (e) {
			console.error("FAILED TO UPLOAD FILE: ", e)
		}
	}
	/**
	 * replaces existing image nodes with imageResize nodes. This keeps backwards compatibility with existing images
	 * in existing documents.
	 *
	 * @param editor - the TipTap Editor instance.
	 */
	const upgradeImageNodes = (editor) => {
		const {state, view} = editor
		const {tr: transaction, schema} = state

		// loop through all the descendant nodes of the Editor's document and run the CB function on them
		state.doc.descendants((node, position) => {
			// if the current node has the type of "image"...
			if(node.type.name === "image") {
				// create a new "imageResize" node and grab the current node's position
				const newNode = schema.nodes.imageResize.create(node.attrs, node.content, node.marks)
				const $pos = transaction.doc.resolve(position)

				// check if the new node can replace the selected node
				if($pos.parent.canReplace($pos.index(), $pos.index() + 1, newNode.type)) {
					// add a new event to the current transaction
					transaction.replaceWith(position, position + node.nodeSize, newNode)
				} else {
					console.warn("Unable to replace image at position ", position, "skipping to next node")
				}
			}
		})

		// if the current transaction results in changes to the current doc...
		if(transaction.docChanged) {
			// apply the transaction to the Editor's current view
			view.dispatch(transaction)
		}
	}
	const editor = useEditor({
		immediatelyRender: true, // we use this to avoid rendering the component before the Provider has synced
		extensions: [
			StarterKit.configure({
				undoRedo: false
			}),
			Highlight.configure({
				multicolor: true,
			}),
			TextStyleKit,
			ImageResize.configure({
				inline: true,
				HTMLAttributes: {
					ondragstart: "this.classList.add('ProseMirror-selectednode')", //ensures selection UI visible when dragging to rearrange
				}
			}),
			// kept for backwards compatibility!
			Image.configure({inline: true}),
			Collaboration.configure({
				document: doc,
			}),
			FileHandler.configure({
				allowedMimeTypes: ["image/png", "image/jpeg", "image/gif", "image/webp"],
				// on user dropping a file into the RTE
				onDrop: async (currentEditor, files, pos) => {
					const embedUrl = await uploadFile(files)
					if(embedUrl) {
						currentEditor.chain().insertContentAt(pos, {
							type: "imageResize",
							attrs: {
								src: embedUrl,
							},
						}).focus().run()
					}
				},
				// on user pasting a file into the RTE
				onPaste: async (currentEditor, files, htmlContent) => {
					if (htmlContent) {
						// if there is htmlContent, stop manual insertion & let other extensions handle insertion
						console.error("HTML content found in paste evt:", htmlContent)
						return
					}

					const embedUrl = await uploadFile(files)
					if(embedUrl) {
						currentEditor.chain().insertContentAt(currentEditor.state.selection.anchor, {
							type: "imageResize",
							attrs: {
								src: embedUrl,
							},
						}).focus().run()
					}
				}
			}),
		],
		onCreate: ({ editor: currentEditor }) => {
			// After the TipTap useEditor hook is mounted to the EditorContent below
			if(provider.authorizedScope === "readonly") {
				setCanEdit(false)
				editor.setEditable(false)
			}

			/* Again, handling these events by binding the handlers in <DocViewer> messes up the reconnect logic,
			 *		see the notes made in that component.
			 *
			 * Binding the handler here doesn't overwrite the previous handler, so we use these to handle effects
			 * 		that only impact the editor (eg: setting it to read-only, displaying the alert banner, etc)
			 */
			provider.on("synced", () => {
				if(!doc.getMap("config").get("initialContentLoaded") && currentEditor) {
					// this should only occur when a document is FIRST loaded.
					currentEditor.commands.setContent(content)
					doc.getMap("config").set("initialContentLoaded", true)
				} else {
					console.info("Document Loaded, initialContent flag found")
					// run the backwards compatibility check on the document's contents
					upgradeImageNodes(currentEditor)
				}
			})

			provider.on("destroy", () => {
				if(doc.getMap("meta").get("deleted") === true || provider.document.getMap("__tiptapcollab__config").get("deleting") === 1){
					currentEditor.setEditable(false, false)
				} else {
					console.error("after provider got destroyed, we couldn't find a deleted flag set to true")
				}
				setDocumentDeleted(true)
			})


		},
		enableContentCheck: true,
		onContentError: ({ editor, error, disableCollaboration }) => {
			disableCollaboration()
			console.error("TIPTAP CONTENT ERROR: ", error)
			console.log("can we see the editor state: ", editor)
			// Leaving this here for debugging. At the moment, there shouldn't be a way to create a content error...
			// ... but as this feature expands, we will probably add schema that didn't used to exist!
		}
	},
	/**
	 * This is a totally undocumented dependency array. Right now, it does nothing, however, it could be exploited...
	 * 		to tear down the existing editor and silently replace it if the doc itself changes
	 */
	//[doc]
	)

	const memoizedEditor = useMemo(() => ({ editor }), [editor])


	return (
		<EditorContext.Provider value={memoizedEditor}>
			<MenuBar isSynching={isSynching} connectionError={connectionError} lastUpdated={lastUpdated} />
			{ !canEdit && (
				<Alert severity="warning">
					This document cannot be edited by you. If you think this is an error, please contact a workspace admin or Refzoom support
				</Alert>
			)}
			{ documentDeleted && (
				<Alert
					severity="error"
					action={
						<Button variant="contained" color="primary" onClick={() => window.close()}> Close tab and return to Dashboard </Button>
					}
				>
					This document has been deleted by a workspace admin
				</Alert>
			)}
			<div className={"content-wrapper"}>
				<EditorContent
					role="presentation"
					className="simple-editor-content"
					editor={editor}
				/>
			</div>
		</EditorContext.Provider>
	)
}